import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
import { START, SEP, STOP, KEY_BITS, decodeKey } from "../libs/proofreading_markers"

// Wraps each marked run in a span carrying its i18n key, and turns a click on
// one into a suggestion.
const MARKED = new RegExp(`${START}(${KEY_BITS})${SEP}([\\s\\S]*?)${STOP}`, "g")
const OPENING = new RegExp(`${START}(${KEY_BITS})${SEP}`)
const ORPHANS = new RegExp(`${START}${KEY_BITS}${SEP}|[${START}${SEP}${STOP}]`, "g")

// Dropping the marker characters alone would leave the key in the text.
const clean = (text) => text.replace(MARKED, "$2").replace(ORPHANS, "")

const NO_SPAN_TAGS = ["OPTION", "TITLE", "TEXTAREA", "SCRIPT", "STYLE"]

const TOAST_MS = 4000

// Not every attribute: rewriting one fires Stimulus value callbacks, and
// turbo-mount answers a props change by remounting the whole Vue tree.
const COPY_ATTRS = /^(title|placeholder|alt|value|content|aria-label)$|^data-.*(content|label|tooltip|text|title|placeholder)/

export default class extends Controller {
  static targets = ["dialog", "key", "oldValue", "proposedValue", "comment", "error", "toggle", "prior", "toast"]
  static values = { endpoint: String, sourcePath: String, locale: String, active: Boolean }

  connect() {
    this.scanHandler = () => this.scan()
    document.addEventListener("turbo:load", this.scanHandler)
    document.addEventListener("turbo:frame-load", this.scanHandler)

    this.keyHandler = (event) => this.#handleKeydown(event)
    document.addEventListener("keydown", this.keyHandler)

    // Fires before the render, so wrap Turbo's own render rather than scanning
    // the DOM that is about to be replaced.
    this.streamHandler = (event) => {
      const render = event.detail.render
      event.detail.render = (streamElement) => {
        render(streamElement)
        this.scan()
      }
    }
    document.addEventListener("turbo:before-stream-render", this.streamHandler)
  }

  // Not connect(): Turbo morphs the page, so starting a session rewrites this
  // attribute in place without reconnecting the controller.
  activeValueChanged() {
    this.#unbindClicks()

    if (this.activeValue) {
      this.clickHandler = (event) => this.#handleClick(event)
      document.addEventListener("click", this.clickHandler, true)
      this.#observe()
    }

    this.scan()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.scanHandler)
    document.removeEventListener("turbo:frame-load", this.scanHandler)
    document.removeEventListener("turbo:before-stream-render", this.streamHandler)
    document.removeEventListener("keydown", this.keyHandler)
    this.#unbindClicks()
    this.observer?.disconnect()
    clearTimeout(this.toastTimer)
  }

  scan() {
    if (!this.activeValue) return

    if (this.scanQueued) return
    this.scanQueued = true

    requestAnimationFrame(() => {
      this.scanQueued = false
      this.#markText(document.documentElement)
      document.querySelectorAll("template").forEach((template) => this.#markText(template.content))
      this.#cleanAttributes()
    })
  }

  close() {
    this.dialogTarget.close()
  }

  async save() {
    const proposal = this.proposedValueTarget.value.trim()
    if (!proposal) return this.close()

    const response = await post(this.endpointValue, {
      body: JSON.stringify({
        translation_suggestion: {
          translation_key: this.keyTarget.textContent,
          locale: this.localeValue,
          old_value: this.oldValueTarget.value,
          proposed_value: proposal,
          comment: this.commentTarget.value.trim(),
          page_url: window.location.pathname
        }
      })
    })

    // request.js resolves on a 4xx, so closing unconditionally would report a
    // rejected suggestion as saved.
    if (response.ok) {
      this.close()
      this.#confirm()
    } else {
      this.errorTarget.textContent = this.errorTarget.dataset.message
    }
  }

  // Private

  // event.code, not event.key: Option+P on macOS produces "π". Submitting the
  // pill's own form keeps the shortcut on the same path as the click.
  #handleKeydown(event) {
    if (!event.altKey || !event.shiftKey || event.code !== "KeyP") return
    if (event.target.closest("input, textarea, select, [contenteditable]")) return

    event.preventDefault()
    this.toggleTarget.requestSubmit()
  }

  #confirm() {
    clearTimeout(this.toastTimer)
    this.toastTarget.classList.remove("hidden")
    this.toastTimer = setTimeout(() => this.toastTarget.classList.add("hidden"), TOAST_MS)
  }

  #unbindClicks() {
    if (!this.clickHandler) return

    document.removeEventListener("click", this.clickHandler, true)
    this.clickHandler = null
  }

  // Vue renders outside Turbo's lifecycle, so no event announces its copy.
  // Re-scanning only on a mutation that carries a marker is self-limiting:
  // wrapping a run removes the markers, so it cannot retrigger itself.
  #observe() {
    this.observer ||= new MutationObserver((records) => {
      if (records.some((record) => this.#carriesMarker(record))) this.scan()
    })

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    })
  }

  #carriesMarker(record) {
    switch (record.type) {
      case "characterData":
        return record.target.nodeValue?.includes(START)
      case "attributes":
        return record.target.getAttribute(record.attributeName)?.includes(START)
      default:
        return Array.from(record.addedNodes).some((node) => node.textContent?.includes(START))
    }
  }

  #markText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.nodeValue.includes(START) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    })

    // Collected first — replacing a node invalidates the walker.
    const nodes = []
    while (walker.nextNode()) nodes.push(walker.currentNode)

    nodes.forEach((node) => this.#markNode(node))
  }

  #markNode(node) {
    const parent = node.parentElement
    const text = node.nodeValue

    // The widget's own chrome is never proofreadable, so it gets no key and no
    // outline. Excluding it by subtree survives renaming the component.
    if (!parent || this.element.contains(parent)) {
      node.nodeValue = clean(text)
      return
    }

    if (NO_SPAN_TAGS.includes(parent.tagName)) return this.#keyOnParent(node, parent, text)

    const fragment = document.createDocumentFragment()
    let index = 0

    for (const match of text.matchAll(MARKED)) {
      const [ full, bits, copy ] = match

      if (match.index > index) fragment.append(clean(text.slice(index, match.index)))
      fragment.append(this.#span(decodeKey(bits), copy))
      index = match.index + full.length
    }

    // A translation containing markup opens here and closes in another node,
    // leaving no complete run to wrap.
    if (index === 0) return this.#keyOnParent(node, parent, text)

    if (index < text.length) fragment.append(clean(text.slice(index)))
    node.replaceWith(fragment)
  }

  #keyOnParent(node, parent, text) {
    node.nodeValue = clean(text)

    const bits = text.match(OPENING)?.[1]
    if (bits && !parent.dataset.i18nKey) parent.dataset.i18nKey = decodeKey(bits)
  }

  #span(key, copy) {
    const span = document.createElement("span")
    span.dataset.i18nKey = key
    span.textContent = clean(copy)
    return span
  }

  #cleanAttributes() {
    document.querySelectorAll("*").forEach((element) => {
      for (const attribute of element.attributes) {
        if (!attribute.value.includes(START) || !COPY_ATTRS.test(attribute.name)) continue
        element.setAttribute(attribute.name, clean(attribute.value))
      }
    })
  }

  #handleClick(event) {
    if (this.element.contains(event.target)) return

    const marked = event.target.closest("[data-i18n-key]")
    if (!marked) return

    // Without the hit test a click anywhere inside an enclosing element is
    // swallowed, and the app becomes unusable while a session is live.
    const topmost = document.elementFromPoint(event.clientX, event.clientY)
    if (!topmost || !marked.contains(topmost)) return

    event.preventDefault()
    event.stopPropagation()

    this.#open(marked)
  }

  async #open(element) {
    const key = element.dataset.i18nKey

    this.keyTarget.textContent = key
    this.errorTarget.textContent = ""
    this.oldValueTarget.value = ""
    this.proposedValueTarget.value = ""
    this.commentTarget.value = ""
    this.priorTarget.replaceChildren()
    this.dialogTarget.showModal()

    const [ source, pending ] = await Promise.all([ this.#source(key), this.#pending(key) ])
    if (this.keyTarget.textContent !== key) return

    this.#renderPending(pending)

    // Never fall back to the text on screen: it has already been interpolated,
    // so saving it back would replace the placeholders with one reviewer's data.
    if (source === null) {
      this.errorTarget.textContent = this.errorTarget.dataset.missing
      return
    }

    this.oldValueTarget.value = source
    this.proposedValueTarget.value = source
    this.proposedValueTarget.focus()
  }

  #renderPending(suggestions) {
    if (!suggestions.length) return

    const heading = document.createElement("p")
    heading.textContent = "Already suggested"
    this.priorTarget.append(heading)

    suggestions.forEach(({ proposed_value, author }) => {
      const line = document.createElement("p")
      line.textContent = author ? `${proposed_value} — ${author}` : proposed_value
      this.priorTarget.append(line)
    })
  }

  async #pending(key) {
    const response = await get(this.endpointValue, {
      query: { translation_key: key, locale: this.localeValue },
      responseKind: "json"
    })

    return response.ok ? await response.json : []
  }

  async #source(key) {
    const path = this.sourcePathValue.replace("__KEY__", encodeURIComponent(key))
    const response = await get(path, { query: { locale: this.localeValue }, responseKind: "json" })
    if (!response.ok) return null

    return (await response.json).source
  }
}
